/**
 * Capacitor native-shell bootstrap.
 *
 * Kept dependency-light and side-effect-free at import time so the web build is
 * unaffected: every entry point short-circuits when not running inside a native
 * Capacitor WebView (`Capacitor.isNativePlatform()` is false in the browser).
 *
 * Responsibilities:
 *  - Tag the document (`html.native`, `html.plt-ios` / `html.plt-android`) so
 *    CSS can special-case the native shells, plus `html.ios-standalone` for the
 *    installed iOS Safari PWA (where Capacitor does NOT run).
 *  - Theme the system bars via the Capacitor 8 core `SystemBars` plugin.
 *  - Disable native keyboard resizing (the visual-viewport logic in
 *    `viewport.ts` owns keyboard handling).
 *  - Seed the safe-area insets into `--sat` / `--sab` / `--sal` / `--sar` for
 *    the iOS PWA (and as a first-frame fallback on native) where
 *    `env(safe-area-*)` can read as 0.
 *
 * Edge-to-edge is native in Capacitor 8: the WebView draws under the system
 * bars and `SystemBars` (config `insetsHandling: "css"`) injects
 * `--safe-area-inset-*` variables. The frontend consumes them via the
 * `--app-safe-*` tokens in `globals.css`, so no native margins are needed.
 */
import { Capacitor } from "@capacitor/core";
import type { SystemBarsStyle } from "@capacitor/core";

import { isIosDevice, isIosStandalonePwa, isStandalonePwa } from "@/lib/pwaInstall";
import { isMobileShellViewport } from "@/lib/viewport";

/** Capacitor 8 SystemBars style tokens. Loaded dynamically so web dev is not blocked by a stale Vite prebundle of `@capacitor/core`. */
type SystemBarStyle = "DARK" | "LIGHT" | "DEFAULT";

/** True when running inside a native Capacitor WebView (iOS/Android). */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/** Installed PWA or Capacitor — layouts that draw under the home indicator. */
export function usesEdgeToEdgeShell(): boolean {
  return isNativePlatform() || isStandalonePwa();
}

/** iPhone X+ home-indicator height when env() is unavailable. */
export const IOS_HOME_INDICATOR_FALLBACK_PX = 34;

/**
 * Resolve the SystemBars style for a theme.
 * `DARK` = light bar content (for a dark background);
 * `LIGHT` = dark bar content (for a light background).
 */
function systemBarsStyleForTheme(theme: "light" | "dark"): SystemBarStyle {
  return theme === "dark" ? "DARK" : "LIGHT";
}

async function setNativeSystemBarsStyle(style: SystemBarStyle): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SystemBars } = await import("@capacitor/core");
    await SystemBars.setStyle({ style: style as SystemBarsStyle });
  } catch {
    /* SystemBars unavailable (e.g. web or stale dev prebundle) */
  }
}

/** Sync the native system-bar style with the resolved app theme. */
export async function syncNativeStatusBarTheme(theme: "light" | "dark"): Promise<void> {
  await setNativeSystemBarsStyle(systemBarsStyleForTheme(theme));
}

/** Tall iOS devices that use the software home indicator instead of a home button. */
export function iosUsesHomeIndicator(): boolean {
  if (!isIosDevice()) return false;
  return Math.max(window.screen.height, window.screen.width) >= 812;
}

/** Fallback bottom inset when CSS env() reads 0 on iOS edge-to-edge shells. */
export function iosHomeIndicatorFallbackPx(): number {
  return iosUsesHomeIndicator() ? IOS_HOME_INDICATOR_FALLBACK_PX : 0;
}

/**
 * Read safe-area insets from CSS env() and Capacitor's injected
 * `--safe-area-inset-*` custom properties (Capacitor 8 `insetsHandling: "css"`).
 */
function readCssSafeAreaInsets(): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;" +
    "padding-top:var(--safe-area-inset-top, env(safe-area-inset-top, 0px));" +
    "padding-bottom:var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));" +
    "padding-left:var(--safe-area-inset-left, env(safe-area-inset-left, 0px));" +
    "padding-right:var(--safe-area-inset-right, env(safe-area-inset-right, 0px));";
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const top = parseFloat(cs.paddingTop) || 0;
  const bottom = parseFloat(cs.paddingBottom) || 0;
  const left = parseFloat(cs.paddingLeft) || 0;
  const right = parseFloat(cs.paddingRight) || 0;
  probe.remove();
  return { top, bottom, left, right };
}

/**
 * Copy the physical safe-area insets onto the document as `--sat` / `--sab` /
 * `--sal` / `--sar`. On native and the iOS standalone PWA,
 * `env(safe-area-inset-*)` can read as 0 for the first frames (or permanently
 * in Safari PWA), so we keep the max of env(), the injected Capacitor value,
 * and the iPhone home-indicator fallback.
 */
export function captureSafeAreaInsets(): void {
  const root = document.documentElement;
  const { top, bottom, left, right } = readCssSafeAreaInsets();

  const prevTop = parseFloat(root.style.getPropertyValue("--sat")) || 0;
  const prevBottom = parseFloat(root.style.getPropertyValue("--sab")) || 0;

  let resolvedBottom = bottom;
  if (usesEdgeToEdgeShell() && isIosDevice()) {
    resolvedBottom = Math.max(resolvedBottom, iosHomeIndicatorFallbackPx());
  }

  root.style.setProperty("--sat", `${Math.max(top, prevTop)}px`);
  root.style.setProperty("--sab", `${Math.max(resolvedBottom, prevBottom)}px`);
  /* Left/right swap on rotate — replace, do not keep the previous max. */
  root.style.setProperty("--sal", `${left}px`);
  root.style.setProperty("--sar", `${right}px`);
}

/** Seed inset fallbacks now, then re-measure as env()/injected values settle. */
function scheduleSafeAreaCapture(): void {
  captureSafeAreaInsets();
  requestAnimationFrame(captureSafeAreaInsets);
  window.setTimeout(captureSafeAreaInsets, 100);
  window.setTimeout(captureSafeAreaInsets, 400);
  window.addEventListener("orientationchange", () => {
    window.setTimeout(captureSafeAreaInsets, 250);
  });
  window.visualViewport?.addEventListener("resize", captureSafeAreaInsets);
}

/**
 * Initialise edge-to-edge mobile layout (installed PWA + Capacitor). Safe to call
 * once at boot before `syncViewportMetrics()`.
 */
export async function initNativeShell(): Promise<void> {
  const root = document.documentElement;
  const edgeToEdge = usesEdgeToEdgeShell();

  if (edgeToEdge && isMobileShellViewport()) {
    root.classList.add("edge-to-edge");
    // iOS Safari's layout viewport excludes the home indicator even with
    // viewport-fit=cover, and Capacitor never runs here. Tag so CSS can grow the
    // document into that inset and viewport.ts can pin the shell height.
    if (isIosStandalonePwa() && !isNativePlatform()) {
      root.classList.add("ios-standalone");
    }
  }

  if (isNativePlatform()) {
    root.classList.add("native");
    root.classList.add(`plt-${Capacitor.getPlatform()}`);

    // Edge-to-edge is the Capacitor 8 default; SystemBars owns styling. Seed a
    // sensible initial style; ThemeContext re-applies the resolved theme.
    await setNativeSystemBarsStyle("DEFAULT");

    // Keyboard must not resize the native WebView — viewport.ts handles it.
    try {
      const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    } catch {
      /* ignore */
    }
  }

  if (!edgeToEdge) return;

  scheduleSafeAreaCapture();
}

/**
 * Hide the native splash screen. Call once the web splash has taken over so
 * there is no flash between the two.
 */
export async function hideNativeSplash(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch {
    /* ignore */
  }
}
